/**
 * The views that cover the page on a phone: the navigation drawer, the right
 * panel and the command palette. Each open view is a history entry with the
 * same URL, so Back closes it (design system README, "On a phone"). The
 * entry keeps the rest of the state of the page under it: ChatPage reads
 * `initialMessage` from it.
 */
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router";

export type PhoneView = "sideNav" | "panel" | "palette";

const PHONE_VIEWS: readonly PhoneView[] = ["sideNav", "panel", "palette"];

type EntryState = Record<string, unknown>;

function asEntryState(state: unknown): EntryState {
  return state !== null && typeof state === "object" ? (state as EntryState) : {};
}

/** The view that the history entry opens, or null. */
export function phoneViewOf(state: unknown): PhoneView | null {
  const entry = asEntryState(state);
  return PHONE_VIEWS.find((view) => entry[view] === true) ?? null;
}

/** The entry state with no view open. */
export function withoutPhoneView(state: unknown): EntryState {
  const next = { ...asEntryState(state) };
  for (const view of PHONE_VIEWS) delete next[view];
  return next;
}

/** The entry state with `view` open. One view is open at a time. */
export function withPhoneView(state: unknown, view: PhoneView): EntryState {
  return { ...withoutPhoneView(state), [view]: true };
}

/** Restore a saved phone panel with a closed history entry below it. */
export function restorePhonePanelLayout(
  location: { pathname: string; search: string; hash: string; state: unknown },
  open: boolean,
  navigate: (to: { pathname: string; search: string; hash: string }, options: { replace?: boolean; state: unknown }) => void,
): EntryState | undefined {
  const current = phoneViewOf(location.state);
  const target = { pathname: location.pathname, search: location.search, hash: location.hash };
  if (open && current !== "panel") {
    if (current !== null) {
      const state = withPhoneView(location.state, "panel");
      navigate(target, { replace: true, state });
      return state;
    }
    const closedSearch = new URLSearchParams(location.search);
    closedSearch.set("panelOpen", "0");
    navigate({ ...target, search: closedSearch.toString() }, { replace: true, state: withoutPhoneView(location.state) });
    const state = withPhoneView(location.state, "panel");
    navigate(target, { state });
    return state;
  }
  if (!open && current === "panel") {
    const state = withoutPhoneView(location.state);
    navigate(target, { replace: true, state });
    return state;
  }
  return undefined;
}

/** Clear reload residue, while allowing an explicit saved panel to open. */
export function useClearInitialPhoneView() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialEntry = useRef({ location, navigate });
  useEffect(() => {
    const { location: loaded, navigate: go } = initialEntry.current;
    if (phoneViewOf(loaded.state) === null) return;
    const episode = /^\/(?:episodes\/[^/]+|datasets\/[^/]+\/episodes\/[^/]+)\/?$/.test(loaded.pathname);
    if (episode && new URLSearchParams(loaded.search).get("panelOpen") === "1") return;
    go({ pathname: loaded.pathname, search: loaded.search, hash: loaded.hash }, {
      replace: true,
      state: withoutPhoneView(loaded.state),
    });
  }, []);
}
